import { motion } from 'framer-motion';
import { useContent } from '@/context/ContentContext';
import { ArrowUpRight } from 'lucide-react';

const IMAGES = [
  'https://images.unsplash.com/photo-1554941829-202a0b2403b8?auto=format&fit=crop&w=1600&q=80',
  'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1600&q=80',
  'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1600&q=80',
  'https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?auto=format&fit=crop&w=1600&q=80',
];

export default function Services() {
  const { t } = useContent();
  if (!t) return null;

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-20 sm:py-24 md:py-40 bg-paper"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 items-start md:items-end mb-12 sm:mb-16 md:mb-24">
          <div className="md:col-span-3 font-mono text-[10px] uppercase tracking-[0.28em] sm:tracking-[0.3em] text-ember">
            § Chapter II · Services
          </div>
          <div className="md:col-span-6">
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tight">
              {t.services.title}
            </h2>
          </div>
          <div className="md:col-span-3 text-ink-dim text-sm md:text-base italic font-serif">
            {t.services.subtitle}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6 md:gap-8">
          {t.services.items.map((item, idx) => {
            const isBig = idx === 0 || idx === 3;
            return (
              <motion.article
                key={idx}
                data-testid={`service-card-${idx}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: idx * 0.08 }}
                className={`relative group ${
                  isBig ? 'md:col-span-8' : 'md:col-span-4'
                } bg-paper-2 border border-[var(--line)] p-5 sm:p-6 md:p-8 clip-frame`}
              >
                <div className="flex items-start justify-between mb-5 sm:mb-6">
                  <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-ember">
                    {item.code}
                  </span>
                  <ArrowUpRight
                    className="w-5 h-5 text-ink-dim group-hover:text-ember transition-colors"
                    strokeWidth={1.4}
                  />
                </div>

                {isBig && (
                  <div className="relative -mx-5 sm:-mx-6 md:-mx-8 mb-6 h-48 sm:h-56 md:h-72 overflow-hidden clip-frame-alt">
                    <img
                      src={IMAGES[idx % IMAGES.length]}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover grayscale-[0.3] contrast-125 brightness-[0.55]"
                    />
                    <div
                      className="absolute inset-0"
                      style={{
                        background:
                          'radial-gradient(circle at 30% 50%, rgba(255,90,31,0.28), transparent 55%), linear-gradient(180deg, transparent 40%, rgba(5,4,3,0.9) 100%)',
                      }}
                    />
                    <div className="absolute bottom-3 sm:bottom-4 left-5 sm:left-6 right-5 sm:right-6 flex items-center justify-between font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.24em] sm:tracking-[0.28em] text-ink-dim">
                      <span>{item.code}</span>
                      <span>◎ Live case</span>
                    </div>
                  </div>
                )}

                <h3 className="font-serif text-xl sm:text-2xl md:text-3xl mb-3 leading-tight">
                  {item.title}
                </h3>
                <p className="text-ink-dim text-sm md:text-base leading-relaxed mb-5 sm:mb-6 max-w-lg">
                  {item.desc}
                </p>
                <ul className="space-y-2 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.18em] sm:tracking-[0.2em] text-ink">
                  {item.bullets?.map((b, bi) => (
                    <li key={bi} className="flex items-center gap-3">
                      <span className="w-4 h-px bg-ember flex-shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
